// src/components/GenerateProductAvatarImageModal.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { ChevronDown, X, Loader2, Plus, Trash2 } from "lucide-react";
import Loader from "./Loader"; // ✅ same loader used elsewhere

const PAGE_SIZE = 20;

const UUID_V4_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuidV4(value) {
  return UUID_V4_REGEX.test(String(value || ""));
}

/* ------------------ Simple Category Dropdown ------------------ */
function CategoryDropdown({ options, value, onChange, label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const selectedLabel =
    options.find((o) => String(o.value) === String(value))?.label ||
    "All Categories";

  return (
    <div className="relative w-full sm:w-64" ref={ref}>
      <label className="block text-xs text-gray-300 mb-1 ml-1">{label}</label>
      <button
        type="button"
        className="flex w-full min-h-[38px] items-center px-3 py-1.5 rounded-lg bg-[#23233a] text-white border transition-all outline-none focus:ring-2 focus:ring-pink-600 border-[#353550]"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="flex-1 text-left truncate">{selectedLabel}</span>
        <ChevronDown
          className={`ml-2 transition-transform ${open ? "rotate-180" : ""}`}
          size={18}
        />
      </button>

      {open && (
        <div className="absolute z-40 mt-1 w-full rounded-xl shadow-xl bg-[#212134] border border-[#353550] max-h-56 overflow-auto animate-fadeIn">
          <div
            className={`flex items-center px-3 py-2 cursor-pointer text-sm hover:bg-[#2e2e45] transition select-none ${
              !value ? "bg-pink-600/10 text-pink-300" : ""
            }`}
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
          >
            All Categories
          </div>
          {options.map((opt) => (
            <div
              key={opt.value}
              className={`flex items-center px-3 py-2 cursor-pointer text-sm hover:bg-[#2e2e45] transition select-none ${
                String(value) === String(opt.value)
                  ? "bg-pink-600/10 text-pink-300"
                  : ""
              }`}
              onClick={() => {
                onChange(String(opt.value));
                setOpen(false);
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------ Avatar Card ------------------------ */
const PublicAvatarCard = React.memo(function PublicAvatarCard({
  avatar,
  isSelected,
  onSelect,
}) {
  return (
    <div
      key={avatar.avatarId}
      className={`relative rounded-lg overflow-hidden border-2 group transition cursor-pointer ${
        isSelected
          ? "border-pink-500 ring-2 ring-pink-600"
          : "border-transparent hover:border-pink-400"
      }`}
      aria-label="Select avatar"
      onClick={() => onSelect(String(avatar.avatarId))}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(String(avatar.avatarId));
        }
      }}
    >
      <div
        className="aspect-[9/16] w-full h-full bg-black"
        style={{ minHeight: 160 }}
      >
        <img
          src={avatar.avatarImagePath}
          alt="Avatar"
          className="w-full h-full object-cover block"
          loading="lazy"
          draggable={false}
        />
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs text-center py-1 truncate z-10">
        {avatar.gender || "Model"}
      </div>
    </div>
  );
});

/* ---------------- Module-scoped avatar cache ----------------- */
// { [projectId+category]: { avatars, page, hasMore, total } }
const avatarCache = {};

export default function GenerateProductAvatarImageModal({
  projectId,
  onClose,
  refreshImages,
}) {
  const { getToken } = useAuth();

  const API_BASE =
    process.env.REACT_APP_BACKEND_URL ||
    process.env.REACT_APP_API_URL ||
    (typeof window !== "undefined" && window._API_BASE) ||
    "";

  // Lock background scroll while modal open
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Stepper
  const [step, setStep] = useState(1);

  // Step 1: assets (ONLY analyzed images)
  const [projectAssets, setProjectAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [assetsError, setAssetsError] = useState("");
  const [selectedAsset, setSelectedAsset] = useState(null);

  // ✅ Step 1 manual upload (copied behavior from Ad Marketing Video modal)
  const [step1UploadedAsset, setStep1UploadedAsset] = useState(null); // { id, url } | null
  const [step1UploadLoading, setStep1UploadLoading] = useState(false);
  const step1FileInputRef = useRef(null);

  // Step 2: avatars
  const [avatars, setAvatars] = useState([]);
  const [avatarPage, setAvatarPage] = useState(1);
  const [hasMoreAvatars, setHasMoreAvatars] = useState(true);
  const [selectedAvatarId, setSelectedAvatarId] = useState(null);
  const [avatarCategory, setAvatarCategory] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [avatarsLoading, setAvatarsLoading] = useState(false);
  const [avatarsError, setAvatarsError] = useState("");
  const [generating, setGenerating] = useState(false);

  const cacheKey = `${projectId || ""}_${avatarCategory || "all"}`;

  // ✅ “page-like loader” flags
  const isAssetsInitialLoading =
    assetsLoading && !assetsError && projectAssets.length === 0;
  const isAvatarsInitialLoading =
    step === 2 && avatarsLoading && !avatarsError && avatars.length === 0;

  /* ----------------------- Load project assets (analyzed only) ----------------------- */
  useEffect(() => {
    let mounted = true;

    async function fetchAnalyzedImages() {
      setAssetsLoading(true);
      setAssetsError("");

      try {
        const token = await getToken();
        // IMPORTANT: use /api/projects/:id (no /full) so we don't get generated assets
        const res = await axios.get(`${API_BASE}/api/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!mounted) return;

        const imgs = (res.data?.images || [])
          .filter((u) => typeof u === "string" && u)
          .map((u, i) => ({
            id: `img-${i}`,
            type: "image",
            url: u,
            name: `image-${i}`,
          }));

        setProjectAssets(imgs);
      } catch {
        if (!mounted) return;
        setAssetsError("Failed to load product images.");
        setProjectAssets([]);
      } finally {
        if (!mounted) return;
        setAssetsLoading(false);
      }
    }

    if (projectId) fetchAnalyzedImages();

    return () => {
      mounted = false;
    };
  }, [API_BASE, getToken, projectId]);

  /* ----------------------- Load avatar categories ----------------------- */
  useEffect(() => {
    let ignore = false;

    async function fetchCategories() {
      try {
        const token = await getToken();
        const res = await axios.get(
          `${API_BASE}/api/product-avatar-images/categories`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!ignore) {
          setCategoryOptions(
            (res.data?.result || []).map((c) => ({
              value: String(c.categoryId),
              label: c.categoryName,
            }))
          );
        }
      } catch {
        if (!ignore) setCategoryOptions([]);
      }
    }

    fetchCategories();

    return () => {
      ignore = true;
    };
  }, [API_BASE, getToken]);

  /* ------------------------ Reset modal state ------------------------ */
  useEffect(() => {
    setStep(1);
    setSelectedAsset(null);
    setStep1UploadedAsset(null);
    setStep1UploadLoading(false);

    setAvatars([]);
    setAvatarPage(1);
    setHasMoreAvatars(true);
    setSelectedAvatarId(null);
    setAvatarCategory("");
    setAvatarsLoading(false);
    setAvatarsError("");
  }, [projectId]);

  /* ------------------------ Helpers: extract API ------------------------ */
  const extractListAndTotal = (result) => {
    const list = result?.data || result?.records || result?.list || [];
    const total =
      result?.total ?? result?.pageTotal ?? result?.totalRecords ?? 0;
    return { list, total: Number(total) || 0 };
  };

  /* ---------------------- Fetch avatars (first page) ---------------------- */
  useEffect(() => {
    if (step !== 2) return;

    let ignore = false;

    async function fetchAvatars() {
      setAvatarsLoading(true);
      setAvatarsError("");

      // Serve from cache if present
      if (avatarCache[cacheKey]) {
        const cached = avatarCache[cacheKey];
        setAvatars(cached.avatars || []);
        setAvatarPage(cached.page || 1);

        setHasMoreAvatars(
          typeof cached.hasMore === "boolean"
            ? cached.hasMore
            : cached.total
            ? (cached.avatars || []).length < cached.total
            : true
        );

        setAvatarsLoading(false);
        return;
      }

      try {
        const token = await getToken();
        const params = { pageNo: 1, pageSize: PAGE_SIZE };
        if (avatarCategory) params.categoryIds = avatarCategory;

        const res = await axios.get(
          `${API_BASE}/api/product-avatar-images/public`,
          { params, headers: { Authorization: `Bearer ${token}` } }
        );

        if (ignore) return;

        const { list, total } = extractListAndTotal(res.data?.result);
        setAvatars(list);
        setAvatarPage(1);

        const hasMore = total ? list.length < total : list.length === PAGE_SIZE;
        setHasMoreAvatars(hasMore);

        avatarCache[cacheKey] = {
          avatars: list,
          page: 1,
          hasMore,
          total,
        };
      } catch {
        if (!ignore) setAvatarsError("Failed to load avatars.");
      } finally {
        if (!ignore) setAvatarsLoading(false);
      }
    }

    fetchAvatars();

    return () => {
      ignore = true;
    };
  }, [step, cacheKey, avatarCategory, getToken, API_BASE]);

  /* --------------------- Fetch more avatars (pagination) --------------------- */
  const fetchMoreAvatars = useCallback(async () => {
    const nextPage = avatarPage + 1;

    setAvatarsLoading(true);
    setAvatarsError("");

    try {
      const token = await getToken();
      const params = { pageNo: nextPage, pageSize: PAGE_SIZE };
      if (avatarCategory) params.categoryIds = avatarCategory;

      const res = await axios.get(
        `${API_BASE}/api/product-avatar-images/public`,
        { params, headers: { Authorization: `Bearer ${token}` } }
      );

      const { list, total } = extractListAndTotal(res.data?.result);

      setAvatars((prev) => {
        const updated = [...prev, ...list];
        const hasMore = total
          ? updated.length < total
          : list.length === PAGE_SIZE;

        avatarCache[cacheKey] = {
          avatars: updated,
          page: nextPage,
          hasMore,
          total,
        };

        return updated;
      });

      setHasMoreAvatars(() => {
        const currentLength = (avatarCache[cacheKey]?.avatars || []).length;
        return total ? currentLength < total : list.length === PAGE_SIZE;
      });

      setAvatarPage(nextPage);
    } catch {
      setAvatarsError("Failed to load more avatars.");
    } finally {
      setAvatarsLoading(false);
    }
  }, [avatarPage, avatarCategory, getToken, cacheKey, API_BASE]);

  /* --------------------- Step 1 manual upload handler (same as Ad Marketing modal) --------------------- */
  const handleStep1Upload = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setStep1UploadLoading(true);
      setAssetsError("");

      try {
        if (file.size > 20 * 1024 * 1024) {
          setAssetsError("Image is too large. Please upload a file under 20MB.");
          return;
        }

        const token = await getToken();
        const formData = new FormData();
        formData.append("file", file);

        // ✅ Reuse the exact same upload endpoint used by Ad Marketing Video
        const { data } = await axios.post(
          `${API_BASE}/api/videos/marketing/endcard/upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const url = data?.url;
        if (url) {
          const newAsset = { id: `asset-upload-${Date.now()}`, url };
          setStep1UploadedAsset(newAsset);
          setSelectedAsset(newAsset); // single-select: auto-select upload
        }
      } catch {
        setAssetsError("Failed to upload image.");
      } finally {
        setStep1UploadLoading(false);
        event.target.value = "";
      }
    },
    [API_BASE, getToken]
  );

  const isUploadedSelected = Boolean(
    step1UploadedAsset &&
      selectedAsset &&
      String(selectedAsset.id || selectedAsset.assetId) ===
        String(step1UploadedAsset.id)
  );

  /* --------------------------- Generate action --------------------------- */
  const handleGenerate = async () => {
    if (!selectedAsset || !selectedAvatarId) return;

    setGenerating(true);
    setAvatarsError("");

    try {
      const token = await getToken();

      const assetIdOrUrl = selectedAsset.id || selectedAsset.assetId || "";

      await axios.post(
        `${API_BASE}/api/product-avatar-images/generate`,
        {
          projectId,
          // ✅ keep API shape; pass UUID if it is one, otherwise pass URL
          assetId: isUuidV4(assetIdOrUrl) ? assetIdOrUrl : selectedAsset.url,
          avatarId: selectedAvatarId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      onClose?.();
      refreshImages?.();
    } catch {
      setAvatarsError("Failed to generate image.");
      setGenerating(false);
    }
  };

  /* ------------------------ StepFooter (same behaviour as Ad Marketing modal) ------------------------ */
  const StepFooter = ({ left, right }) => (
    <div className="absolute bottom-0 left-0 w-full bg-[#191930] border-t border-[#2B2B4A] flex justify-between items-center px-8 py-4 z-20">
      <div>{left}</div>
      <div className="flex items-center gap-3">{right}</div>
    </div>
  );

  /* ----------------------------- Render ----------------------------- */
  const filteredAnalyzedImages = projectAssets.filter(
    (a) =>
      (a.type && ["photo", "image"].includes(a.type)) ||
      (a.url && a.url.match(/\.(jpg|jpeg|png|webp)$/i))
  );

  return (
    <div className="fixed z-50 inset-0 flex items-center justify-center bg-black/60 px-2 py-4">
      <div className="relative bg-[#191930] w-[880px] max-w-[98vw] h-[82vh] rounded-2xl shadow-2xl flex flex-col min-h-[520px] overflow-hidden">
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 bg-white text-black p-2 rounded-full hover:bg-gray-100 z-10"
          disabled={generating}
          aria-label="Close modal"
        >
          <X size={22} />
        </button>

        {/* Stepper (header) */}
        <div className="flex items-center gap-2 px-8 pt-7 pb-2 shrink-0">
          <span
            className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold ${
              step === 1
                ? "bg-pink-600 text-white"
                : "bg-[#29294a] text-gray-400"
            }`}
          >
            1
          </span>
          <span className="text-base sm:text-lg text-white font-semibold mr-8">
            Select Product Asset
          </span>
          <span
            className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold ${
              step === 2
                ? "bg-pink-600 text-white"
                : "bg-[#29294a] text-gray-400"
            }`}
          >
            2
          </span>
          <span className="text-base sm:text-lg text-white font-semibold">
            Select Avatar
          </span>
        </div>

        {/* Scrollable content area */}
        <div
          id="product-avatar-image-modal-scroll"
          className="flex-1 flex flex-col px-8 pt-2 pb-32 min-h-0 overflow-y-auto"
        >
          {/* Step 1: Assets */}
          {step === 1 && (
            <>
              <p className="mb-4 text-gray-300 flex items-center gap-4">
                Select one product image to use for the avatar image.
                <span className="text-gray-400 text-sm">
                  Selected {selectedAsset ? 1 : 0} / 1
                </span>
              </p>

              {/* ✅ hidden manual upload input */}
              <input
                ref={step1FileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleStep1Upload}
              />

              {isAssetsInitialLoading ? (
                <div className="flex items-center justify-center py-10 sm:py-16 select-none">
                  <Loader />
                </div>
              ) : assetsLoading ? (
                <div className="grid grid-cols-5 gap-4">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div
                      key={i}
                      className="rounded-lg border border-[#2B2B4A] bg-[#23233a] aspect-[4/5] h-[150px] md:h-[170px] animate-pulse"
                    />
                  ))}
                </div>
              ) : filteredAnalyzedImages.length === 0 && !step1UploadedAsset ? (
                <div className="text-white/70 text-center py-10 select-none">
                  No product images found. Analyze your product to extract images
                  first.
                </div>
              ) : (
                <div className="rounded-lg border border-[#23233a] bg-[#191930] px-4 py-3">
                  <div className="grid grid-cols-5 gap-4">
                    {/* ✅ Upload tile (exact manual upload flow from Ad Marketing Video modal) */}
                    <div
                      className={`relative rounded-lg overflow-hidden border-2 group transition ${
                        step1UploadedAsset
                          ? isUploadedSelected
                            ? "border-pink-500 ring-2 ring-pink-600 cursor-pointer"
                            : "border-transparent hover:border-pink-400 cursor-pointer"
                          : "border-[#3A3A4A] border-dashed hover:border-pink-400 cursor-pointer"
                      }`}
                      onClick={() => {
                        if (generating || step1UploadLoading) return;

                        if (step1UploadedAsset) {
                          setSelectedAsset(step1UploadedAsset);
                          return;
                        }

                        step1FileInputRef.current?.click();
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          if (generating || step1UploadLoading) return;

                          if (step1UploadedAsset) {
                            setSelectedAsset(step1UploadedAsset);
                            return;
                          }

                          step1FileInputRef.current?.click();
                        }
                      }}
                      aria-label={
                        step1UploadedAsset ? "Uploaded asset" : "Upload image"
                      }
                    >
                      <div className="aspect-[4/5] bg-black relative">
                        {step1UploadedAsset ? (
                          <img
                            className="object-cover w-full h-full"
                            src={step1UploadedAsset.url}
                            alt="Uploaded asset"
                            loading="lazy"
                            draggable={false}
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-[#151529]">
                            <div className="w-12 h-12 rounded-full bg-[#23233a] border border-[#2B2B4A] flex items-center justify-center mb-3">
                              <Plus size={22} className="text-white" />
                            </div>
                            <div className="text-white font-semibold text-sm">
                              Upload image
                            </div>
                            <div className="text-gray-400 text-[11px] mt-1 px-3 text-center">
                              JPG, PNG, WEBP
                            </div>
                          </div>
                        )}

                        {step1UploadLoading && (
                          <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                            <Loader2 className="animate-spin text-white" />
                          </div>
                        )}

                        <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                      </div>

                      {/* Actions (only when uploaded) */}
                      {step1UploadedAsset && (
                        <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition">
                          <button
                            type="button"
                            className="px-2.5 py-1.5 rounded-md bg-black/70 hover:bg-black/85 text-white text-xs font-semibold border border-white/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (generating || step1UploadLoading) return;
                              step1FileInputRef.current?.click();
                            }}
                            aria-label="Replace uploaded image"
                            disabled={generating || step1UploadLoading}
                          >
                            Replace
                          </button>
                          <button
                            type="button"
                            className="p-2 rounded-md bg-black/70 hover:bg-black/85 text-white border border-white/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (generating) return;

                              const uploadedId = step1UploadedAsset.id;
                              setStep1UploadedAsset(null);

                              setSelectedAsset((prev) => {
                                const prevId = prev?.id || prev?.assetId;
                                return String(prevId) === String(uploadedId)
                                  ? null
                                  : prev;
                              });
                            }}
                            aria-label="Remove uploaded image"
                            disabled={generating}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Existing analyzed images */}
                    {filteredAnalyzedImages.map((asset) => {
                      const assetId = asset.id || asset.assetId;
                      const isSelected =
                        selectedAsset &&
                        String(selectedAsset.id || selectedAsset.assetId) ===
                          String(assetId);

                      return (
                        <div
                          key={assetId}
                          className={`relative rounded-lg overflow-hidden border-2 cursor-pointer group transition ${
                            isSelected
                              ? "border-pink-500 ring-2 ring-pink-600"
                              : "border-transparent hover:border-pink-400"
                          }`}
                          onClick={() => setSelectedAsset(asset)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setSelectedAsset(asset);
                            }
                          }}
                        >
                          <div className="aspect-[4/5] bg-black">
                            <img
                              src={asset.url || asset.assetUrl}
                              alt={asset.name || "Asset"}
                              className="object-cover w-full h-full"
                              loading="lazy"
                              draggable={false}
                            />
                          </div>
                          <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {assetsError && (
                <div className="mt-3 text-red-400 text-sm">{assetsError}</div>
              )}
            </>
          )}

          {/* Step 2: Avatars */}
          {step === 2 && (
            <>
              <h2 className="text-xl font-bold text-white mb-4">
                Choose an avatar
              </h2>

              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div className="flex flex-wrap items-start gap-4">
                  <CategoryDropdown
                    label="Category"
                    options={categoryOptions}
                    value={avatarCategory}
                    onChange={(cat) => {
                      const nextCat = String(cat || "");
                      setAvatarCategory(nextCat);
                      setSelectedAvatarId(null);
                      setAvatars([]);
                      setAvatarPage(1);
                      setHasMoreAvatars(true);

                      // ✅ clear cache for this category
                      delete avatarCache[`${projectId || ""}_${nextCat || "all"}`];
                    }}
                  />
                </div>
              </div>

              {isAvatarsInitialLoading ? (
                <div className="flex items-center justify-center py-10 sm:py-16 select-none">
                  <Loader />
                </div>
              ) : (
                <InfiniteScroll
                  dataLength={avatars.length}
                  next={fetchMoreAvatars}
                  hasMore={hasMoreAvatars}
                  loader={
                    avatarsLoading && hasMoreAvatars ? (
                      <div className="w-full flex justify-center py-4">
                        <Loader />
                      </div>
                    ) : null
                  }
                  scrollableTarget="product-avatar-image-modal-scroll"
                  scrollThreshold={0.85}
                >
                  <div className="grid grid-cols-5 gap-4 pb-4">
                    {avatars.map((avatar) => (
                      <PublicAvatarCard
                        key={avatar.avatarId}
                        avatar={avatar}
                        isSelected={
                          String(selectedAvatarId) === String(avatar.avatarId)
                        }
                        onSelect={setSelectedAvatarId}
                      />
                    ))}
                  </div>
                </InfiniteScroll>
              )}

              {avatarsError && (
                <div className="text-red-400 text-center py-2">{avatarsError}</div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <StepFooter
          left={
            <button
              type="button"
              disabled={step === 1 || generating}
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              className="rounded-lg font-semibold px-6 py-2 bg-[#2B2B4A] hover:bg-[#3D3D6E] text-white disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Previous
            </button>
          }
          right={
            step < 2 ? (
              <button
                type="button"
                onClick={() => setStep((s) => Math.min(2, s + 1))}
                disabled={generating || !selectedAsset}
                className="rounded-lg bg-pink-600 font-semibold px-6 py-2 hover:bg-pink-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={handleGenerate}
                disabled={!selectedAvatarId || generating}
                className="rounded-lg bg-pink-600 font-semibold px-6 py-2 hover:bg-pink-700 text-white flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {generating && <Loader2 className="animate-spin mr-2" size={18} />}
                {generating ? "Generating..." : "Generate Image"}
              </button>
            )
          }
        />
      </div>
    </div>
  );
}
